import React, { useEffect, useState } from 'react';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { onSnapshot as onSnapshotListen } from '../utils/firestoreListen';
import AccountConsentSettings from './AccountConsentSettings';
import { getOrderStatusLabel, getOrderStatusColor } from '../utils/orderStatus';

export default function AccountSettingsModal({ isOpen, onClose, initialTab = 'consent' }) {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab); // 'consent' | 'orders'

  useEffect(() => {
    if (isOpen) setActiveTab(initialTab);
  }, [isOpen, initialTab]);

  // Real-time orders for this user (same source as admin; when admin updates status it updates here)
  useEffect(() => {
    if (!isOpen || !user?.uid) {
      setOrders([]);
      setOrdersLoading(false);
      return;
    }
    setOrdersLoading(true);
    const q = query(collection(db, 'orders'), where('userId', '==', user.uid));
    const unsub = onSnapshotListen(
      q,
      (snap) => {
        const data = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const tA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const tB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return tB - tA;
          });
        setOrders(data);
        setOrdersLoading(false);
      },
      (err) => {
        console.warn('AccountSettingsModal orders listener error:', err);
        setOrdersLoading(false);
      },
      { purpose: 'AccountSettingsModal-orders', component: 'AccountSettingsModal' }
    );
    return () => unsub();
  }, [isOpen, user?.uid]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
          <h2 className="text-xl font-bold">Account Settings</h2>
          <button onClick={onClose} className="text-white/90 hover:text-white text-2xl leading-none">×</button>
        </div>
        {/* Tabs */}
        <div className="flex border-b border-amber-200 flex-shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('consent')}
            className={`px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'consent' ? 'text-amber-700 border-b-2 border-amber-500 bg-amber-50/50' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Consent &amp; Privacy
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('orders')}
            className={`px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'orders' ? 'text-amber-700 border-b-2 border-amber-500 bg-amber-50/50' : 'text-gray-600 hover:text-gray-900'}`}
          >
            My Orders
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          {activeTab === 'consent' && <AccountConsentSettings />}
          {activeTab === 'orders' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">All orders you placed. Status is updated when your order is processed.</p>
              {ordersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8 rounded-xl bg-amber-50/50 border border-amber-200 text-gray-600">
                  No orders yet. Your orders will appear here after you place them.
                </div>
              ) : (
                <ul className="space-y-3">
                  {orders.map((order) => {
                    const created = order.createdAt ? new Date(order.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—';
                    const statusLabel = getOrderStatusLabel(order.status);
                    const statusColor = getOrderStatusColor(order.status);
                    const shortId = order.id ? order.id.slice(-8).toUpperCase() : '—';
                    const total = order.total != null ? `₹${Number(order.total).toLocaleString()}` : '—';
                    const itemSummary = order.items?.length ? order.items.map((i) => i.title || i.id).slice(0, 2).join(', ') + (order.items.length > 2 ? '…' : '') : 'Order';
                    return (
                      <li
                        key={order.id}
                        className="p-4 rounded-xl border border-amber-200 bg-white hover:bg-amber-50/30 transition-colors"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <span className="font-semibold text-gray-900">Order #{shortId}</span>
                            <span className="text-gray-500 text-sm ml-2">{created}</span>
                          </div>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                            {statusLabel}
                          </span>
                        </div>
                        <div className="mt-2 text-sm text-gray-600">
                          {itemSummary}
                        </div>
                        <div className="mt-2 text-sm font-medium text-gray-900">
                          Total: {total}
                          {order.printMode && <span className="text-amber-700 ml-2">(Print)</span>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
